import { createFileRoute, Link } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { DashboardShell } from "@/components/DashboardShell";
import { PARTNER_NAV } from "@/lib/nav";
import { SERVICES, SERVICE_STATUS_META, isServiceLive, useMyServices } from "@/lib/services";

export const Route = createFileRoute("/_authenticated/partner/services/")({
  head: () => ({
    meta: [
      { title: "My services · Landoo Partner" },
      {
        name: "description",
        content: "Manage your active, pending and expired Landoo partner services.",
      },
    ],
  }),
  component: ServicesPage,
});

function ServicesPage() {
  const { profiles, isLoading } = useMyServices();

  const active = profiles.filter(isServiceLive);
  const pending = profiles.filter((p) =>
    [
      "registration_draft",
      "verification_pending",
      "agreement_pending",
      "payment_pending",
      "submitted",
      "pending_approval",
    ].includes(p.service_status),
  );
  const expired = profiles.filter(
    (p) => p.service_status === "expired" || (p.service_status === "active" && !isServiceLive(p)),
  );
  const inactive = SERVICES.filter((s) => !profiles.some((p) => p.service_type === s.value));

  return (
    <DashboardShell title="My services" role="Partner" items={PARTNER_NAV}>
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Each service has its own registration, verification, agreement, subscription and approval.
        </p>
        <Link
          to="/partner/services/add"
          className="inline-flex items-center gap-1.5 rounded-full bg-coral px-3.5 py-2 text-xs font-semibold text-white shadow-soft"
        >
          <Plus className="h-4 w-4" /> Add new service
        </Link>
      </div>

      {isLoading && <div className="mt-4 text-sm text-muted-foreground">Loading services…</div>}

      <Section title="Active services" empty="No active services yet.">
        {active.map((p) => (
          <ServiceRow
            key={p.id}
            type={p.service_type}
            status={p.service_status}
            note={p.is_primary ? "Primary service" : "Add-on service"}
          />
        ))}
      </Section>

      <Section title="Pending service requests" empty="No pending requests.">
        {pending.map((p) => (
          <ServiceRow
            key={p.id}
            type={p.service_type}
            status={p.service_status}
            note={p.admin_notes ?? "Awaiting admin approval"}
          />
        ))}
      </Section>

      <Section title="Expired services" empty="No expired services.">
        {expired.map((p) => (
          <ServiceRow
            key={p.id}
            type={p.service_type}
            status="expired"
            note="Renew to continue listing"
          />
        ))}
      </Section>

      <Section title="Available add-on services" empty="All services activated.">
        {inactive.map((s) => (
          <ServiceRow key={s.value} type={s.value} status="not_activated" note={s.blurb} />
        ))}
      </Section>
    </DashboardShell>
  );
}

function Section({
  title,
  empty,
  children,
}: {
  title: string;
  empty: string;
  children: React.ReactNode[];
}) {
  return (
    <div className="mt-6">
      <h2 className="text-base font-bold text-brown-dark">{title}</h2>
      {children.length === 0 ? (
        <div className="mt-2 rounded-2xl border border-dashed border-border bg-card/60 p-4 text-xs text-muted-foreground">
          {empty}
        </div>
      ) : (
        <div className="mt-2 grid gap-2 sm:grid-cols-2">{children}</div>
      )}
    </div>
  );
}

function ServiceRow({
  type,
  status,
  note,
}: {
  type: "sale" | "rent" | "lease" | "parking";
  status: keyof typeof SERVICE_STATUS_META;
  note: string;
}) {
  const meta = SERVICE_STATUS_META[status];
  const service = SERVICES.find((s) => s.value === type)!;
  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-4 shadow-soft">
      <div>
        <div className="text-sm font-bold text-brown-dark">{service.cardTitle}</div>
        <div className="text-[11px] text-muted-foreground">{note}</div>
        <span
          className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-[10px] font-semibold ${meta.cls}`}
        >
          {meta.label}
        </span>
      </div>
      <Link
        to="/partner/services/$serviceType"
        params={{ serviceType: type }}
        className="rounded-full border border-border px-3 py-1.5 text-[11px] font-semibold text-brown-dark hover:bg-brand-soft"
      >
        {status === "not_activated" ? "Add service" : status === "expired" ? "Renew" : "View"}
      </Link>
    </div>
  );
}
